import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { TextField } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { useHistory, useParams } from 'react-router-dom';
import BreadCrumb from '../../../components/Breadcrumbs';
import Alert from '../../../components/common/Alert';
import Button from '../../../components/common/Button';
import StatusIndicator from '../../../components/common/StatusIndicator';
import Title from '../../../components/common/Title';
import CatalogApi from '../catalog-api';
import {
  CATEGORY_OPTIONS,
  getCatalogOccasionLabel,
  getCatalogOccasionOptions,
} from '../constants';
import { USER_STATUS_CLASSNAMES } from '../../ClientContainer/constants';

const STATUS_METADATA = {
  pendiente: {
    label: 'Pendiente',
    className: USER_STATUS_CLASSNAMES.deactivated,
  },
  aprobada: {
    label: 'Aprobada',
    className: USER_STATUS_CLASSNAMES.active,
  },
  rechazada: {
    label: 'Rechazada',
    className: USER_STATUS_CLASSNAMES.rejected,
  },
};

const formatValue = (value) => {
  if (Array.isArray(value)) {
    return value.length ? value.join(', ') : '-';
  }

  if (value === null || value === undefined || value === '') {
    return '-';
  }

  return value;
};

const normalizeStringArray = (value) =>
  Array.isArray(value)
    ? [...new Set(
        value
          .filter((item) => typeof item === 'string')
          .map((item) => item.trim())
          .filter(Boolean)
      )]
    : [];

const areStringArraysEqual = (left, right) => {
  const normalizedLeft = [...normalizeStringArray(left)].sort();
  const normalizedRight = [...normalizeStringArray(right)].sort();

  if (normalizedLeft.length !== normalizedRight.length) {
    return false;
  }

  return normalizedLeft.every((value, index) => value === normalizedRight[index]);
};

const normalizeString = (value) =>
  typeof value === 'string' ? value.trim() : '';

const renderLinkValue = (value) => {
  if (!value) {
    return <span className="catalog-detail__field-value">-</span>;
  }

  return (
    <a
      className="catalog-detail__field-link"
      href={value}
      target="_blank"
      rel="noreferrer"
    >
      {value}
    </a>
  );
};

const DetailField = ({
  label,
  value,
  isLink = false,
  preserveBreaks = false,
  fullWidth = false,
}) => (
  <div
    className={`catalog-detail__field ${
      fullWidth ? 'catalog-detail__field--full' : ''
    }`}
  >
    <span className="catalog-detail__field-label">{label}</span>
    {isLink ? (
      renderLinkValue(value)
    ) : (
      <span
        className={`catalog-detail__field-value ${
          preserveBreaks ? 'catalog-detail__field-value--multiline' : ''
        }`}
      >
        {formatValue(value)}
      </span>
    )}
  </div>
);

const CatalogDetailContainer = () => {
  const history = useHistory();
  const { userId } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [postulation, setPostulation] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedFeaturedContexts, setSelectedFeaturedContexts] = useState([]);
  const [featuredContextOptions, setFeaturedContextOptions] = useState([]);
  const [isLoadingFeaturedContextOptions, setIsLoadingFeaturedContextOptions] =
    useState(true);
  const [featuredContextOptionsError, setFeaturedContextOptionsError] =
    useState('');
  const [isSavingClassification, setIsSavingClassification] = useState(false);
  const [classificationEditorError, setClassificationEditorError] =
    useState('');
  const [message, setMessage] = useState('');
  const [typeMessage, setTypeMessage] = useState('info');

  const fetchPostulation = useCallback(async () => {
    try {
      const {
        data: { postulation: fetchedPostulation },
      } = await CatalogApi.fetchPostulationByUser(userId);

      setPostulation(fetchedPostulation);
      setSelectedCategory(normalizeString(fetchedPostulation?.category));
      setSelectedFeaturedContexts(
        normalizeStringArray(fetchedPostulation?.featuredContexts)
      );
      setClassificationEditorError('');
    } catch (error) {
      setMessage(
        error?.data?.message ||
          'Ocurrió un error al cargar el detalle de la postulación.'
      );
      setTypeMessage('error');
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchPostulation();
  }, [fetchPostulation]);

  useEffect(() => {
    let isMounted = true;

    const loadFeaturedContextOptions = async () => {
      try {
        setIsLoadingFeaturedContextOptions(true);
        setFeaturedContextOptionsError('');
        const {
          data: { occasions = [] } = {},
        } = await CatalogApi.fetchOccasions();

        if (!isMounted) {
          return;
        }

        setFeaturedContextOptions(getCatalogOccasionOptions(occasions));
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setFeaturedContextOptionsError(
          error?.data?.message ||
            'Ocurrió un error al cargar las ocasiones del catálogo.'
        );
      } finally {
        if (isMounted) {
          setIsLoadingFeaturedContextOptions(false);
        }
      }
    };

    loadFeaturedContextOptions();

    return () => {
      isMounted = false;
    };
  }, []);

  const statusIndicator = useMemo(() => {
    if (!postulation?.status) {
      return null;
    }

    const metadata =
      STATUS_METADATA[postulation.status] || STATUS_METADATA.pendiente;

    return (
      <StatusIndicator
        statusClassName={metadata.className}
        text={metadata.label}
      />
    );
  }, [postulation]);

  const selectedFeaturedContextOptions = useMemo(() => {
    const selectedContextNames = new Set(selectedFeaturedContexts);

    return featuredContextOptions.filter((occasion) =>
      selectedContextNames.has(occasion.name)
    );
  }, [featuredContextOptions, selectedFeaturedContexts]);

  const availableCategoryOptions = useMemo(
    () =>
      [...new Set([
        ...CATEGORY_OPTIONS,
        normalizeString(postulation?.category),
        normalizeString(selectedCategory),
      ].filter(Boolean))],
    [postulation, selectedCategory]
  );

  const hasCategoryChanges = useMemo(
    () => normalizeString(selectedCategory) !== normalizeString(postulation?.category),
    [postulation, selectedCategory]
  );

  const hasFeaturedContextChanges = useMemo(
    () =>
      !areStringArraysEqual(
        selectedFeaturedContexts,
        postulation?.featuredContexts
      ),
    [postulation, selectedFeaturedContexts]
  );

  const hasClassificationChanges = useMemo(
    () => hasCategoryChanges || hasFeaturedContextChanges,
    [hasCategoryChanges, hasFeaturedContextChanges]
  );

  const saveClassification = useCallback(async () => {
    if (!postulation?.id || isSavingClassification || !hasClassificationChanges) {
      return;
    }

    const nextCategory = normalizeString(selectedCategory);
    const nextFeaturedContexts = normalizeStringArray(selectedFeaturedContexts);

    if (!nextCategory) {
      setClassificationEditorError('Debes seleccionar una categoría.');
      return;
    }

    if (!nextFeaturedContexts.length) {
      setClassificationEditorError('Debes seleccionar al menos un contexto.');
      return;
    }

    try {
      setIsSavingClassification(true);
      setClassificationEditorError('');

      const {
        data: { postulation: updatedPostulation },
      } = await CatalogApi.updatePostulationClassification(postulation.id, {
        category: nextCategory,
        featuredContexts: nextFeaturedContexts,
      });

      setPostulation(updatedPostulation);
      setSelectedCategory(normalizeString(updatedPostulation?.category));
      setSelectedFeaturedContexts(
        normalizeStringArray(updatedPostulation?.featuredContexts)
      );
      setClassificationEditorError('');
      setMessage('Clasificación de la postulación actualizada con éxito.');
      setTypeMessage('success');
    } catch (error) {
      setClassificationEditorError(
        error?.data?.message ||
          'Ocurrió un error al actualizar la clasificación de la postulación.'
      );
      setMessage(
        error?.data?.message ||
          'Ocurrió un error al actualizar la clasificación de la postulación.'
      );
      setTypeMessage('error');
    } finally {
      setIsSavingClassification(false);
    }
  }, [
    hasClassificationChanges,
    isSavingClassification,
    postulation,
    selectedCategory,
    selectedFeaturedContexts,
  ]);

  const links = [
    { url: '/catalogo', text: 'Catálogo' },
    { url: '/catalogo', text: 'Detalle de postulación' },
  ];

  const formattedCreatedAt = postulation?.createdAt
    ? moment(postulation.createdAt).format('DD/MM/YYYY HH:mm')
    : '-';

  const formattedUpdatedAt = postulation?.updatedAt
    ? moment(postulation.updatedAt).format('DD/MM/YYYY HH:mm')
    : '-';

  return (
    <div className="content-panel user-container catalog-detail">
      <BreadCrumb links={links} />
      {message && <Alert type={typeMessage}>{message}</Alert>}
      {featuredContextOptionsError && (
        <Alert type="error">{featuredContextOptionsError}</Alert>
      )}
      <div className="catalog-detail__header">
        <div>
          <Title title="Detalle de postulación" />
          <span className="subtitle">
            Revisa todos los datos enviados por el usuario para su postulación
            al catálogo.
          </span>
        </div>
        <Button secondary onClick={() => history.push('/catalogo')}>
          Volver
        </Button>
      </div>

      {isLoading ? (
        <div className="catalog-detail__card">
          <span className="subtitle">
            Cargando detalle de la postulación...
          </span>
        </div>
      ) : null}

      {!isLoading && postulation ? (
        <>
          <div className="catalog-detail__top-grid">
            <div className="catalog-detail__card">
              <h2 className="catalog-detail__section-title">
                Datos del usuario
              </h2>
              <div className="catalog-detail__grid">
                <DetailField
                  label="Nombre y apellido"
                  value={postulation.userName}
                />
                <DetailField label="Email" value={postulation.userEmail} />
                <DetailField
                  label="Número de celular"
                  value={postulation.userCelphone}
                />
              </div>
            </div>

            <div className="catalog-detail__card catalog-detail__card--editor">
              <h2 className="catalog-detail__section-title">
                Edición de postulación
              </h2>
              <div className="catalog-detail__editor">
                <div className="catalog-detail__filter-control">
                  <Autocomplete
                    options={availableCategoryOptions}
                    getOptionLabel={(option) => option || ''}
                    value={selectedCategory || null}
                    onChange={(event, newValue) => {
                      setClassificationEditorError('');
                      setSelectedCategory(normalizeString(newValue));
                    }}
                    fullWidth
                    clearOnEscape
                    autoHighlight
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        label="Categoría"
                        placeholder="Selecciona una categoría"
                        size="small"
                      />
                    )}
                  />
                </div>
                <div className="catalog-detail__filter-control">
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    options={featuredContextOptions}
                    getOptionLabel={getCatalogOccasionLabel}
                    getOptionSelected={(option, value) =>
                      Boolean(option) &&
                      Boolean(value) &&
                      option.name === value.name
                    }
                    value={selectedFeaturedContextOptions}
                    onChange={(event, newValue) => {
                      setClassificationEditorError('');
                      setSelectedFeaturedContexts(
                        normalizeStringArray(
                          newValue.map((occasion) => occasion.name)
                        )
                      );
                    }}
                    fullWidth
                    clearOnEscape
                    autoHighlight
                    disabled={
                      isLoadingFeaturedContextOptions ||
                      Boolean(featuredContextOptionsError)
                    }
                    loading={isLoadingFeaturedContextOptions}
                    loadingText="Cargando ocasiones..."
                    noOptionsText="No hay ocasiones creadas"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        label="Contextos"
                        placeholder="Selecciona contextos"
                        size="small"
                      />
                    )}
                  />
                </div>
                {classificationEditorError ? (
                  <span className="catalog-detail__editor-error">
                    {classificationEditorError}
                  </span>
                ) : null}
                <div className="catalog-detail__editor-actions">
                  <Button
                    onClick={saveClassification}
                    disabled={
                      isSavingClassification ||
                      !hasClassificationChanges
                    }
                    loading={isSavingClassification}
                  >
                    Guardar
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className="catalog-detail__card">
            <h2 className="catalog-detail__section-title">Resumen</h2>
            <div className="catalog-detail__grid">
              <div className="catalog-detail__field">
                <span className="catalog-detail__field-label">Estado</span>
                <div className="catalog-detail__status">{statusIndicator}</div>
              </div>
              <DetailField label="Fecha de envío" value={formattedCreatedAt} />
              <DetailField
                label="Última actualización"
                value={formattedUpdatedAt}
              />
              {postulation.status === 'rechazada' ? (
                <DetailField
                  label="Motivo de rechazo"
                  value={postulation.rejectionReason}
                  preserveBreaks
                  fullWidth
                />
              ) : null}
            </div>
          </div>

          <div className="catalog-detail__card">
            <h2 className="catalog-detail__section-title">
              Información del emprendimiento
            </h2>
            <div className="catalog-detail__grid">
              <DetailField
                label="Nombre del emprendimiento"
                value={postulation.companyName}
              />
              <DetailField label="Categoría" value={postulation.category} />
              <DetailField
                label="Contextos adecuados"
                value={postulation.featuredContexts}
              />
              <DetailField
                label="Sitio web"
                value={postulation.website}
                isLink
              />
              <DetailField
                label="Descripción del emprendimiento"
                value={postulation.companyDescription}
                preserveBreaks
                fullWidth
              />
            </div>
          </div>

          <div className="catalog-detail__card">
            <h2 className="catalog-detail__section-title">
              Canales y propuesta comercial
            </h2>
            <div className="catalog-detail__grid">
              <DetailField
                label="Instagram"
                value={postulation.instagram}
                isLink
              />
              <DetailField
                label="Facebook"
                value={postulation.facebook}
                isLink
              />
              <DetailField
                label="Propuesta comercial"
                value={postulation.commercialOffer}
                preserveBreaks
                fullWidth
              />
            </div>
          </div>

          <div className="catalog-detail__card">
            <h2 className="catalog-detail__section-title">Archivos</h2>
            <div className="catalog-detail__media-section">
              <div className="catalog-detail__media-card">
                <span className="catalog-detail__field-label">Logo</span>
                <div className="catalog-detail__gallery catalog-detail__gallery--logo">
                  <a
                    href={
                      postulation.logoImagePreviewUrl || postulation.logoImage
                    }
                    target="_blank"
                    rel="noreferrer"
                    className="catalog-detail__image-link"
                  >
                    <img
                      className="catalog-detail__image"
                      src={
                        postulation.logoImagePreviewUrl || postulation.logoImage
                      }
                      alt="Logo del emprendimiento"
                    />
                  </a>
                </div>
              </div>
              <div className="catalog-detail__media-card">
                <span className="catalog-detail__field-label">Galería</span>
                <div className="catalog-detail__gallery">
                  {(postulation.galleryImages || []).map((imageUrl, index) => (
                    <a
                      key={imageUrl}
                      href={
                        postulation.galleryImagePreviewUrls?.[index] || imageUrl
                      }
                      target="_blank"
                      rel="noreferrer"
                      className="catalog-detail__image-link"
                    >
                      <img
                        className="catalog-detail__image"
                        src={
                          postulation.galleryImagePreviewUrls?.[index] ||
                          imageUrl
                        }
                        alt={`Imagen de galería ${index + 1}`}
                      />
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default CatalogDetailContainer;
